// EcgCard.jsx
import React from "react";
import moment from "moment";
import CenterType from "../CenterType";
import { Box, Card, CardActionArea, Typography } from "@mui/material";

export const EcgCard = ({ selectreport, data, is_current, isOpened }) => {
    const isUnread = !isOpened;

    const created = moment(Number(data.createdAt));
    const diffInMinutes = moment().diff(created, "minutes");

    const formatPatientName = () => {
        const first = (data?.patient_details?.firstName || "").trim();
        const last = (data?.patient_details?.lastName || "").trim();

        const format = (v) =>
            v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : "";

        return `${format(first)} ${format(last)}`.trim();
    };

    const getRequestStatusStyles = () => {
        const status = data?.interpretation_request_status;

        if (status === "requested" || status === "in progress") {
            return {
                label: "Awaiting",
                bg: "#FFF3E0",
                color: "#F48C06",
                border: "1px solid #DC760761",
            };
        }

        if (status === "completed") {
            return {
                label: "Interpreted",
                bg: "#E4F6E9",
                color: "#2E7D32",
                border: "1px solid #3485301F",
            };
        }

        return {
            label: "Not Interpreted",
            bg: "#FFE4E4",
            color: "#D32F2F",
            border: "1px solid #0000000A",
        };
    };

    const getReportStatusStyles = () => {
        const status = data?.interpretation_report_status;
        if (status === "normal") return { label: "Normal ECG", color: "#2E7D32" };
        if (status === "abnormal")
            return { label: "Abnormal ECG", color: "#C77800" };
        if (status === "retake") return { label: "Retake ECG", color: "#0277BD" };
        if (status === "critical")
            return { label: "Critical ECG", color: "#D34848" };
        if (status === "beyond_critical")
            return { label: "Beyond Critical", color: "#D34848" };

        return null;
    };

    const renderCreatedAt = () => {
        let text;
        if (diffInMinutes < 1) {
            text = "Just now";
        } else if (diffInMinutes < 60) {
            text = created.fromNow();
        } else {
            text = created.format("lll");
        }

        return (
            <Box
                component="span"
                sx={{
                    fontSize: 13,
                    fontWeight: isUnread ? 700 : 400,
                    display: "inline-flex",
                    alignItems: "center",
                }}
            >
                {text}
                {isUnread && (
                    <Box
                        component="span"
                        sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            bgcolor: "#514f6e",
                            ml: 0.75,
                        }}
                    />
                )}
            </Box>
        );
    };

    const {
        label: reqLabel,
        bg: reqBg,
        color: reqColor,
        border: reqBorder,
    } = getRequestStatusStyles();
    const reportStatus = getReportStatusStyles();

    return (
        <Card
            elevation={0}
            sx={{
                height: "104px",
                mb: 2,
                borderRadius: 3,
                border: "2px solid",
                borderColor: is_current ? "#5751E9" : "var(--app-border)",
                bgcolor: isUnread
                    ? "var(--app-unread)" // unread = subtle tint
                    : "var(--app-surface)", // read = surface
                boxShadow: "var(--app-shadow)",
                transition: "border 0.3s, background-color 0.3s",
            }}
        >
            <CardActionArea
                onClick={() => {
                    selectreport(data.id);
                }}
                sx={{ p: 1.5 }}
            >
                {/* Top row */}
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    {/* Left side (ID + Name) */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            minWidth: 0,
                            flex: 1, // IMPORTANT
                            overflow: "hidden", // IMPORTANT
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 17,
                                fontWeight: 700,
                                fontFamily: "figtree",
                                flexShrink: 0,
                            }}
                        >
                            #{data?.collection_id}
                        </Typography>

                        <Typography sx={{ mx: 0.5, fontSize: 17, flexShrink: 0 }}>
                            |
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: 600,
                                ml: 0.5,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                minWidth: 0,
                                flex: 1, // IMPORTANT
                            }}
                        >
                            {formatPatientName()}
                        </Typography>
                    </Box>

                    {/* Right side label */}
                    <Box
                        sx={{
                            px: 1,
                            py: 0.4,
                            borderRadius: 1,
                            bgcolor: reqBg,
                            color: reqColor,
                            border: reqBorder,
                            fontSize: 12,
                            fontWeight: 600,
                            fontFamily: "Figtree",
                            flexShrink: 0,
                        }}
                    >
                        {reqLabel}
                    </Box>
                </Box>

                {/* Center */}
                <Typography
                    sx={{
                        mt: 0.75,
                        fontSize: 13,
                        fontWeight: isUnread ? 700 : 400,
                        color: "var(--app-text-muted)",
                    }}
                >
                    <CenterType data={data} />
                </Typography>

                {/* Bottom */}
                <Box
                    sx={{
                        mt: 1.25,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography sx={{ fontSize: 12, color: "var(--app-text-muted)" }}>
                        {renderCreatedAt()}
                    </Typography>

                    {reportStatus && (
                        <Typography
                            sx={{ fontSize: 15, fontWeight: 700, color: reportStatus.color }}
                        >
                            {reportStatus.label}
                        </Typography>
                    )}
                </Box>
            </CardActionArea>
        </Card>
    );
};